import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { TimerProvider } from "@/context/TimerContext";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AppLayout from "@/components/layout/AppLayout";
import PermGuard from "@/components/layout/PermGuard";
import Dashboard from "@/pages/Dashboard";
import Empresas from "@/pages/crm/Empresas";
import EmpresaDetalle from "@/pages/crm/EmpresaDetalle";
import Contactos from "@/pages/crm/Contactos";
import Proveedores from "@/pages/crm/Proveedores";
import Proyectos from "@/pages/projects/Proyectos";
import ProyectoDetalle from "@/pages/projects/ProyectoDetalle";
import PropuestaCliente from "@/pages/projects/PropuestaCliente";
import PresupuestosList from "@/pages/projects/PresupuestosList";
import CierresReport from "@/pages/projects/CierresReport";
import Tareas from "@/pages/Tareas";
import Horas from "@/pages/Horas";
import PagosExternos from "@/pages/pagos/PagosExternos";
import Permisos from "@/pages/admin/Permisos";
import Ajustes from "@/pages/admin/Ajustes";
import Backups from "@/pages/admin/Backups";
import Presupuestos from "@/pages/crm/Presupuestos";
// Portal del empleado + RRHH (Fase 3 · Laboral)
import MiFicha from "@/pages/rrhh/MiFicha";
import Vacaciones from "@/pages/rrhh/Vacaciones";
import Nominas from "@/pages/rrhh/Nominas";
import Equipo from "@/pages/rrhh/Equipo";
import EquipoDetalle from "@/pages/rrhh/EquipoDetalle";
import RegistroHorarioPage from "@/pages/rrhh/RegistroHorarioPage";
import SolicitudesVacaciones from "@/pages/rrhh/SolicitudesVacaciones";
import Festivos from "@/pages/rrhh/Festivos";
import MiAgenda from "@/pages/rrhh/MiAgenda";
import ManualUso from "@/pages/ManualUso";
import CalendarioEquipo from "@/pages/rrhh/CalendarioEquipo";
import RoleGuard from "@/components/layout/RoleGuard";
import GastosFijos from "@/pages/tesoreria/GastosFijos";
import CobrosPagos from "@/pages/tesoreria/CobrosPagos";
import GestionNominas from "@/pages/rrhh/GestionNominas";
import ResumenSalarios from "@/pages/rrhh/ResumenSalarios";
// Módulo Restauración / Catering (portado de Dinning_Tool)
import DiningWrapper from "@/pages/dining/DiningWrapper";
import DiningDashboard from "@/pages/dining/Dashboard";
import DiningRestaurants from "@/pages/dining/Restaurants";
import DiningRestaurantDetail from "@/pages/dining/RestaurantDetail";
import DiningEvents from "@/pages/dining/Events";
import DiningAttendance from "@/pages/dining/Attendance";
import DiningInvoices from "@/pages/dining/Invoices";
import DiningCashout from "@/pages/dining/Cashout";
import DiningPresupuesto from "@/pages/dining/Presupuesto";
import DiningEmployees from "@/pages/dining/Employees";
import DiningImportExcel from "@/pages/dining/ImportExcel";
import DiningWaves from "@/pages/dining/Waves";
import DiningAudit from "@/pages/dining/Audit";
import DiningSend from "@/pages/dining/SendToProjects";

const CASHFLOW_ROLES = ["Administrador", "Administración"];

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback (session_id in URL fragment) before anything else.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route element={<AppLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/crm/empresas" element={<PermGuard module="crm"><Empresas /></PermGuard>} />
        <Route path="/crm/empresa/:id" element={<PermGuard module="crm"><EmpresaDetalle /></PermGuard>} />
        <Route path="/crm/contactos" element={<PermGuard module="crm"><Contactos /></PermGuard>} />
        <Route path="/crm/proveedores" element={<PermGuard module="crm"><Proveedores /></PermGuard>} />
        <Route path="/crm/presupuestos" element={<PermGuard module="crm"><Presupuestos /></PermGuard>} />
        <Route path="/proyectos" element={<PermGuard module="projects"><Proyectos /></PermGuard>} />
        <Route path="/presupuestos" element={<PermGuard module="projects"><PresupuestosList /></PermGuard>} />
        <Route path="/informes/cierres" element={<RoleGuard roles={["Administrador", "Directora"]}><CierresReport /></RoleGuard>} />
        <Route path="/proyecto/:id" element={<PermGuard module="projects"><ProyectoDetalle /></PermGuard>} />
        <Route path="/proyecto/:id/propuesta" element={<PermGuard module="projects"><PropuestaCliente /></PermGuard>} />
        <Route path="/tareas" element={<PermGuard module="tasks"><Tareas /></PermGuard>} />
        <Route path="/horas" element={<PermGuard module="timesheets"><Horas /></PermGuard>} />
        <Route path="/pagos-externos" element={<PermGuard module="external_payments"><PagosExternos /></PermGuard>} />
        <Route path="/tesoreria/gastos-fijos" element={<RoleGuard roles={CASHFLOW_ROLES}><GastosFijos /></RoleGuard>} />
        <Route path="/tesoreria/cobros-pagos" element={<RoleGuard roles={["Administrador", "Directora", "Administración"]}><CobrosPagos /></RoleGuard>} />
        <Route path="/portal/mi-ficha" element={<MiFicha />} />
        <Route path="/portal/vacaciones" element={<Vacaciones />} />
        <Route path="/portal/nominas" element={<Nominas />} />
        <Route path="/portal/agenda" element={<MiAgenda />} />
        <Route path="/portal/festivos" element={<Festivos />} />
        <Route path="/rrhh/equipo" element={<PermGuard module="hr"><Equipo /></PermGuard>} />
        <Route path="/rrhh/equipo/:uid" element={<PermGuard module="hr"><EquipoDetalle /></PermGuard>} />
        <Route path="/rrhh/registro-horario" element={<RegistroHorarioPage />} />
        <Route path="/rrhh/nominas" element={<PermGuard module="hr"><GestionNominas /></PermGuard>} />
        <Route path="/rrhh/resumen-salarios" element={<PermGuard module="hr"><ResumenSalarios /></PermGuard>} />
        <Route path="/rrhh/vacaciones" element={<PermGuard module="hr"><SolicitudesVacaciones /></PermGuard>} />
        <Route path="/rrhh/calendario" element={<PermGuard module="hr"><CalendarioEquipo /></PermGuard>} />
        <Route path="/rrhh/festivos" element={<PermGuard module="hr"><Festivos /></PermGuard>} />
        <Route path="/usuarios" element={<Navigate to="/rrhh/equipo" replace />} />
        <Route path="/permisos" element={<PermGuard module="settings"><Permisos /></PermGuard>} />
        <Route path="/ajustes" element={<PermGuard module="settings"><Ajustes /></PermGuard>} />
        <Route path="/copias-seguridad" element={<RoleGuard roles={["Administrador"]}><Backups /></RoleGuard>} />
        <Route path="/manual" element={<ManualUso />} />
        <Route path="/restauracion" element={<PermGuard module="dining"><DiningWrapper /></PermGuard>}>
          <Route index element={<Navigate to="/restauracion/dashboard" replace />} />
          <Route path="dashboard" element={<DiningDashboard />} />
          <Route path="restaurantes" element={<DiningRestaurants />} />
          <Route path="restaurantes/:id" element={<DiningRestaurantDetail />} />
          <Route path="eventos" element={<DiningEvents />} />
          <Route path="asistencia" element={<DiningAttendance />} />
          <Route path="facturas" element={<DiningInvoices />} />
          <Route path="cashout" element={<DiningCashout />} />
          <Route path="presupuesto" element={<DiningPresupuesto />} />
          <Route path="empleados" element={<DiningEmployees />} />
          <Route path="enviar" element={<DiningSend />} />
          <Route path="importar" element={<DiningImportExcel />} />
          <Route path="waves" element={<DiningWaves />} />
          <Route path="auditoria" element={<DiningAudit />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <TimerProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </TimerProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
